import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ExternalLink, ArrowRight, Tag } from 'lucide-react'
import { WORK } from '../constants/testIds'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const fallbackProjects = [
  {
    slug: "sadar-bazaar", title: "Sadar Bazaar — Punjab Imports", subtitle: "Full Store IT Delivery",
    category: "Infrastructure & Implementation",
    tags: ["CCTV", "Security Alarms", "PA & Audio", "Data Cabling", "POS", "Email Hosting"],
    description: "End-to-end IT infrastructure delivery for a new retail store from zero.",
    challenge: "A new retail store with no existing IT infrastructure needed a complete technology stack deployed under tight timeline constraints.",
    solution: "Designed and delivered: CCTV surveillance, security alarms, PA & music, data cabling, POS, and professional email hosting.",
    outcome: "Store opened with fully operational IT infrastructure. Zero technology delays on launch day.",
    metrics: [{ value: "6", label: "Systems Deployed" }, { value: "0", label: "Launch Delays" }, { value: "1", label: "Integrator" }],
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80", order: 1,
  },
  {
    slug: "majnu", title: "Majnu Shisha Bar & Lounge", subtitle: "POS + Digital Ordering Platform",
    category: "Software & POS",
    tags: ["POS", "Web App", "Admin Dashboard", "Client Ordering"],
    description: "Complete digital ordering system for a Melbourne hospitality venue.",
    challenge: "Manual ordering processes limited service speed and created operational inefficiencies.",
    solution: "Built dual-interface: staff POS and customer-facing ordering app at app.majnu.com.au.",
    outcome: "Live system currently serving customers.",
    metrics: [{ value: "2", label: "Interfaces" }, { value: "Live", label: "Status" }, { value: "Full", label: "Admin" }],
    live_url: "https://app.majnu.com.au/table/MAJNU001",
    image: "https://images.unsplash.com/photo-1567521464027-f127ff144326?w=800&q=80", order: 2,
  },
  {
    slug: "crm-hrms", title: "In-House CRM & HRMS", subtitle: "Custom Internal Systems",
    category: "Software Development",
    tags: ["CRM", "HRMS", "Multi-store", "React", "FastAPI"],
    description: "Custom CRM and HRMS for a Melbourne supermarket group.",
    challenge: "Growing multi-store group lacked unified systems.",
    solution: "Building unified platform: customer management, employee onboarding, performance tracking.",
    outcome: "In active development. Architecture finalized.",
    metrics: [{ value: "4+", label: "Stores" }, { value: "2", label: "Systems" }, { value: "WIP", label: "Status" }],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80", order: 3,
  },
  {
    slug: "multi-store-ops", title: "Multi-Store IT Operations", subtitle: "4-Store Management",
    category: "IT Operations",
    tags: ["IT Management", "Multi-store", "Network", "Support"],
    description: "Ongoing IT operations management across 4 retail stores.",
    challenge: "Maintaining consistent IT across locations with varying infrastructure complexity.",
    solution: "Standardized stack, proactive maintenance, rapid-response protocols.",
    outcome: "4 stores on consistent, monitored infrastructure.",
    metrics: [{ value: "4", label: "Stores" }, { value: "Proactive", label: "Approach" }, { value: "24/7", label: "Support" }],
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80", order: 4,
  }
]

export default function CaseStudies() {
  const [projects, setProjects] = useState(fallbackProjects)
  const [expanded, setExpanded] = useState(null)

  useEffect(() => {
    fetch(`${API}/projects`).then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.length) setProjects(d) }).catch(() => {})
  }, [])

  return (
    <section id="work" data-testid={WORK.section} className="py-16 md:py-24 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }} className="flex items-center gap-3 mb-5">
          <span className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase">Case Studies</span>
          <span className="h-px flex-1 max-w-[60px] bg-[#0047AB]/25" />
        </motion.div>

        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.05 }}
          className="font-sans font-bold text-4xl md:text-5xl tracking-tight text-[#111111] mb-4">
          Shipped.
          <br /><span className="text-[#6B7280]">Real clients. Real outcomes.</span>
        </motion.h2>

        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.15 }}
          className="font-body text-[#6B7280] text-base md:text-lg max-w-xl mb-10">
          Every project below was designed, delivered, and supported by me — end to end.
        </motion.p>

        <div className="space-y-3">
          {projects.map((project, i) => (
            <ProjectRow key={project.slug} project={project} index={i}
              isOpen={expanded === i} onToggle={() => setExpanded(expanded === i ? null : i)} />
          ))}
        </div>
      </div>
    </section>
  )
}

function ProjectRow({ project, index, isOpen, onToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: index * 0.06 }}
      className={`border rounded-xl bg-white transition-all duration-300 ${
        isOpen ? 'border-[#0047AB]/30 shadow-card-hover' : 'border-[#E5E7EB] shadow-card hover:border-[#D1D5DB] hover:shadow-card-hover'
      }`}
      data-testid={`case-study-${project.slug}`}
    >
      {/* Row header */}
      <button onClick={onToggle} aria-expanded={isOpen}
        className="w-full flex items-center gap-4 p-6 md:p-7 text-left group">
        <span className={`font-mono text-sm shrink-0 transition-colors duration-200 ${isOpen ? 'text-[#0047AB]' : 'text-[#9CA3AF] group-hover:text-[#0047AB]'}`}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <span className={`font-sans font-semibold text-lg md:text-xl transition-colors duration-200 ${isOpen ? 'text-[#0047AB]' : 'text-[#111111] group-hover:text-[#0047AB]'}`}>
              {project.title}
            </span>
            <span className="font-body text-[#9CA3AF] text-sm hidden sm:inline">{project.subtitle}</span>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {(project.tags || []).slice(0, 3).map(tag => (
            <span key={tag} className="px-2 py-1 text-xs font-mono text-[#9CA3AF] border border-[#E5E7EB] rounded-md bg-[#F3F4F6]">
              {tag}
            </span>
          ))}
        </div>
        <span className={`shrink-0 ml-2 transition-transform duration-300 ${isOpen ? 'rotate-90 text-[#0047AB]' : 'text-[#9CA3AF]'}`}>
          <ArrowRight size={16} />
        </span>
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 md:px-7 pb-8 border-t border-[#E5E7EB]">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 pt-7">
                <div className="lg:col-span-2">
                  <img src={project.image} alt={project.title}
                    className="w-full aspect-video object-cover rounded-xl border border-[#E5E7EB]" loading="lazy" />
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {(project.metrics || []).map((m, i) => (
                      <div key={i} className="text-center p-3 border border-[#E5E7EB] rounded-xl bg-[#FAFAFA]">
                        <div className="font-mono text-[#0047AB] text-lg font-bold">{m.value}</div>
                        <div className="font-body text-[#9CA3AF] text-xs mt-0.5">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-3 space-y-5">
                  <CaseBlock label="Context" text={project.description} />
                  <CaseBlock label="Challenge" text={project.challenge} />
                  <CaseBlock label="Solution" text={project.solution} />
                  <CaseBlock label="Outcome" text={project.outcome} accent />
                  <div className="flex flex-wrap gap-2">
                    {(project.tags || []).map(tag => (
                      <span key={tag} className="flex items-center gap-1 px-2.5 py-1 text-xs font-mono text-[#6B7280] border border-[#E5E7EB] rounded-md bg-[#F3F4F6]">
                        <Tag size={8} />{tag}
                      </span>
                    ))}
                  </div>
                  {project.live_url && (
                    <a href={project.live_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#0047AB]/25 text-[#0047AB] text-sm font-mono rounded-lg hover:bg-[#EBF2FF] transition-colors duration-200"
                      data-testid={`case-study-live-${project.slug}`}>
                      <ExternalLink size={12} />
                      View Live — {project.live_url.replace('https://', '')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function CaseBlock({ label, text, accent = false }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-1.5">{label}</div>
      <p className={`font-body text-sm leading-relaxed ${accent ? 'text-[#374151]' : 'text-[#6B7280]'}`}>{text}</p>
    </div>
  )
}
